/**
 * mdxBridge — lossless-by-construction Markdown/MDX  ->  editor HTML converter.
 *
 * The block editor stores Markdown/MDX text. On load we split the source into
 * top-level segments:
 *   - plain Markdown            -> rendered with `marked` (GFM, tables, code meta)
 *   - a JSX/HTML component block -> turned into a REAL editor node *only if* the
 *     bridge fully understands every attribute and child (so serializing the node
 *     reproduces the same component), otherwise it becomes an atomic `rawMdx`
 *     node that keeps the source verbatim.
 *   - inline JSX/HTML tags the editor cannot represent (<appname>, <Icon/>, ...)
 *     become atomic `rawInline` nodes.
 * Nothing is ever silently dropped.
 */
import { Marked } from 'marked';
import { extractRaggedTables } from './tableGuard';

// ------------------------------------------------------------------ helpers
export const escAttr = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const escText = escAttr;

const unescAttr = (s) =>
  String(s ?? '')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');

const marked = new Marked({ gfm: true, breaks: false });
marked.use({
  // never auto-link bare URLs / emails: the source text must stay as written
  tokenizer: { url() { return undefined; } },
  renderer: {
    // Keep the fence info string: first word = language, remainder = meta
    // (title="..", {1,3}, showLineNumbers ...) so nothing is lost.
    code({ text, lang }) {
      const info = (lang || '').trim();
      const m = info.match(/^(\S+)\s*([\s\S]*)$/);
      const language = m ? m[1] : '';
      const meta = m ? m[2].trim() : '';
      return `<pre><code${language ? ` class="language-${escAttr(language)}"` : ''}${
        meta ? ` data-meta="${escAttr(meta)}"` : ''
      }>${escText(text)}</code></pre>\n`;
    },
  },
});

const VOID_TAGS = new Set(['br', 'hr', 'img', 'input', 'meta', 'link', 'source', 'wbr', 'col', 'area', 'base', 'embed', 'track']);
// Inline HTML the editor represents natively (marks / hard breaks / images)
const SAFE_INLINE = new Set(['a', 'b', 'i', 'em', 'strong', 'u', 's', 'del', 'strike', 'code', 'br', 'img']);

const CALLOUT_TAGS = ['Callout', 'Note', 'Info', 'Tip', 'Warning', 'Caution', 'Error', 'Danger', 'Success', 'Check'];

// ------------------------------------------------------- protected ranges
/** Ranges of fenced code blocks + inline code spans (never scanned for tags). */
function protectedRanges(src) {
  const ranges = [];
  const lines = src.split('\n');
  let off = 0;
  let fence = null; // { ch, len, start }
  for (const line of lines) {
    const m = /^ {0,3}(`{3,}|~{3,})/.exec(line);
    if (fence) {
      if (m && m[1][0] === fence.ch && m[1].length >= fence.len && /^ {0,3}[`~]+\s*$/.test(line)) {
        ranges.push([fence.start, off + line.length]);
        fence = null;
      }
    } else if (m) {
      fence = { ch: m[1][0], len: m[1].length, start: off };
    }
    off += line.length + 1;
  }
  if (fence) ranges.push([fence.start, src.length]);
  const re = /(`+)[^`\n]*?\1/g;
  let mm;
  while ((mm = re.exec(src))) ranges.push([mm.index, mm.index + mm[0].length]);
  ranges.sort((a, b) => a[0] - b[0]);
  return ranges;
}

const inRanges = (ranges, i) => {
  for (const [a, b] of ranges) {
    if (i < a) return false;
    if (i >= a && i < b) return true;
  }
  return false;
};

// ------------------------------------------------------------ tag parsing
const NAME_RE = /[A-Za-z][\w.:-]*/y;
const ATTR_RE = /[A-Za-z_:][\w:.-]*/y;

/** Parse an opening tag at src[i] === '<'. Returns null when not a well-formed tag. */
export function parseOpenTag(src, i) {
  if (src[i] !== '<') return null;
  NAME_RE.lastIndex = i + 1;
  const nm = NAME_RE.exec(src);
  if (!nm) return null;
  let p = NAME_RE.lastIndex;
  const attrs = {};
  const order = [];
  for (;;) {
    while (p < src.length && /\s/.test(src[p])) p++;
    if (p >= src.length) return null;
    if (src[p] === '>') return { name: nm[0], attrs, order, selfClosing: false, start: i, end: p + 1 };
    if (src[p] === '/' && src[p + 1] === '>') return { name: nm[0], attrs, order, selfClosing: true, start: i, end: p + 2 };
    ATTR_RE.lastIndex = p;
    const am = ATTR_RE.exec(src);
    if (!am) return null;
    p = ATTR_RE.lastIndex;
    let val = true;
    if (src[p] === '=') {
      p++;
      const q = src[p];
      if (q === '"' || q === "'") {
        const e = src.indexOf(q, p + 1);
        if (e < 0) return null;
        val = unescAttr(src.slice(p + 1, e));
        p = e + 1;
      } else if (q === '{') {
        let d = 0;
        let e = p;
        for (; e < src.length; e++) {
          if (src[e] === '{') d++;
          else if (src[e] === '}') {
            d--;
            if (!d) break;
          }
        }
        if (d) return null;
        val = { expr: src.slice(p + 1, e).trim() };
        p = e + 1;
      } else return null;
    }
    attrs[am[0]] = val;
    order.push(am[0]);
  }
}

/** Find the matching close tag for an already-opened `name` starting at `from`. */
function findClose(src, from, name, ranges) {
  const re = new RegExp(`<(/?)${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=[\\s/>])`, 'g');
  re.lastIndex = from;
  let depth = 1;
  let m;
  while ((m = re.exec(src))) {
    if (inRanges(ranges, m.index)) continue;
    if (m[1] === '/') {
      const gt = src.indexOf('>', m.index);
      if (gt < 0) return null;
      depth--;
      if (depth === 0) return { closeStart: m.index, closeEnd: gt + 1 };
      re.lastIndex = gt + 1;
    } else {
      const t = parseOpenTag(src, m.index);
      if (t && !t.selfClosing) depth++;
      if (t) re.lastIndex = t.end;
    }
  }
  return null;
}

const lineEnd = (src, i) => {
  const e = src.indexOf('\n', i);
  return e < 0 ? src.length : e;
};

/** dedent common leading whitespace and trim blank edges */
export function dedent(text) {
  const lines = text.replace(/^\s*\n/, '').replace(/\s+$/, '').split('\n');
  let min = Infinity;
  for (const l of lines) {
    if (!l.trim()) continue;
    const ind = /^[ \t]*/.exec(l)[0].length;
    if (ind < min) min = ind;
  }
  if (!isFinite(min) || min === 0) return lines.join('\n');
  return lines.map((l) => l.slice(Math.min(min, /^[ \t]*/.exec(l)[0].length))).join('\n');
}

// ------------------------------------------------------------- segmenter
/**
 * Split source into [{type:'md',text} | {type:'jsx', raw, tag, open, inner, hasClose}]
 * Only top-level (line-start) component/HTML blocks are extracted.
 */
export function splitBlocks(src) {
  const ranges = protectedRanges(src);
  const segs = [];
  let mdStart = 0;
  let pos = 0;
  let prevBlank = true;
  while (pos < src.length) {
    const le = lineEnd(src, pos);
    const line = src.slice(pos, le);
    const lead = /^ {0,3}/.exec(line)[0].length;
    const idx = pos + lead;
    let handled = false;
    if (src[idx] === '<' && !inRanges(ranges, idx)) {
      const upper = /[A-Z]/.test(src[idx + 1] || '');
      if (src.startsWith('<!--', idx) && (prevBlank || lead === 0)) {
        const e = src.indexOf('-->', idx + 4);
        const end = e < 0 ? le : lineEnd(src, e + 3);
        segs.push({ type: 'md', text: src.slice(mdStart, pos) });
        segs.push({ type: 'jsx', raw: src.slice(idx, end), tag: '!--', open: null, inner: '', hasClose: false });
        pos = end + 1;
        mdStart = pos;
        prevBlank = false;
        handled = true;
      } else if (prevBlank || upper) {
        const open = parseOpenTag(src, idx);
        if (open) {
          let end;
          let inner = '';
          let hasClose = false;
          // exact case: <Col> is a component, <col> is a void html tag
          const lname = open.name;
          if (open.selfClosing || VOID_TAGS.has(lname)) {
            end = open.end;
          } else {
            const close = findClose(src, open.end, open.name, ranges);
            if (close) {
              end = close.closeEnd;
              inner = src.slice(open.end, close.closeStart);
              hasClose = true;
            } else {
              end = open.end;
            }
          }
          end = lineEnd(src, end);
          if (!upper) {
            // lowercase html: keep the whole paragraph (until blank line) verbatim
            const rest = src.slice(end);
            const bl = rest.search(/\n[ \t]*\n/);
            end = bl < 0 ? src.length : end + bl;
          }
          if (!hasClose && !open.selfClosing && !VOID_TAGS.has(lname) && upper) {
            // unclosed component: swallow until blank line so we never half-parse it
            const rest = src.slice(end);
            const bl = rest.search(/\n[ \t]*\n/);
            end = bl < 0 ? src.length : end + bl;
          }
          segs.push({ type: 'md', text: src.slice(mdStart, pos) });
          segs.push({ type: 'jsx', raw: src.slice(idx, end), tag: open.name, open, inner, hasClose });
          pos = end + 1;
          mdStart = pos;
          prevBlank = false;
          handled = true;
        }
      }
    }
    if (!handled) {
      prevBlank = line.trim() === '';
      pos = le + 1;
    }
  }
  segs.push({ type: 'md', text: src.slice(mdStart) });
  return segs.filter((s) => s.type !== 'md' || s.text.trim() !== '');
}

// ------------------------------------------------------ inline raw handling
const INLINE_TAG_RE = /<!--[\s\S]*?-->|<\/?[A-Za-z][\w.:-]*(?:\s(?:"[^"]*"|'[^']*'|\{[^}]*\}|[^<>"'{}])*)?\/?>/g;

function mdInline(text) {
  // protect inline JSX/HTML the editor has no mark for, outside code
  const ranges = protectedRanges(text);
  const raws = [];
  let out = '';
  let last = 0;
  let m;
  INLINE_TAG_RE.lastIndex = 0;
  while ((m = INLINE_TAG_RE.exec(text))) {
    if (inRanges(ranges, m.index)) continue;
    const nm = /^<\/?([A-Za-z][\w.:-]*)/.exec(m[0]);
    if (nm && SAFE_INLINE.has(nm[1])) continue;
    out += text.slice(last, m.index) + `RAWINLINE${raws.length}X`;
    raws.push(m[0]);
    last = m.index + m[0].length;
  }
  out += text.slice(last);
  const ragged = extractRaggedTables(out, (t) => marked.lexer(t));
  out = ragged.text;
  let html = marked.parse(out);
  ragged.tables.forEach((raw, i) => {
    html = html.replace(new RegExp(`<p>\\s*RAWTABLE${i}X\\s*</p>`), rawNode(raw));
  });
  // GFM task lists -> TipTap taskList / taskItem markup
  html = html
    .replace(/<ul>(\s*<li>(?:<p>)?<input [^>]*type="checkbox")/g, '<ul data-type="taskList">$1')
    .replace(/<li>(<p>)?<input ([^>]*)type="checkbox"([^>]*)>\s?/g, (m, p, a, b) => {
      const checked = /checked/.test(a + b);
      return `<li data-type="taskItem" data-checked="${checked}">${p || ''}`;
    });
  raws.forEach((raw, i) => {
    html = html.split(`RAWINLINE${i}X`).join(`<span data-type="raw-inline" data-source="${escAttr(raw)}"></span>`);
  });
  return html;
}

// ----------------------------------------------------------- conversion
const attrStr = (v) => (typeof v === 'string' ? v : null);
const attrNum = (v) => {
  if (v && typeof v === 'object' && v.expr !== undefined) v = v.expr;
  if (typeof v === 'string' && /^\d+$/.test(v.trim())) return parseInt(v, 10);
  return null;
};
const onlyAttrs = (open, allowed) => open.order.every((k) => allowed.includes(k));

const rawNode = (raw) => `<div data-type="raw-mdx" data-source="${escAttr(raw)}"></div>`;

/** children of a component: whitespace-only between; returns array of jsx segs or null */
function childBlocks(inner, names) {
  const segs = splitBlocks(inner);
  const out = [];
  for (const s of segs) {
    if (s.type === 'md') {
      if (s.text.trim() !== '') return null;
      continue;
    }
    if (!names.includes(s.tag) || !s.hasClose && !s.open?.selfClosing) return null;
    out.push(s);
  }
  return out;
}

const bodyHtml = (inner) => {
  const t = dedent(inner);
  return t ? mdToHtml(t) : '<p></p>';
};

function convertBlock(seg) {
  const { tag, open, inner, raw } = seg;
  try {
    // ---- callouts (Callout / Note / Info / Tip / Warning / ...)
    if (CALLOUT_TAGS.includes(tag) && seg.hasClose) {
      const allowed = tag === 'Callout' ? ['type', 'title'] : ['title'];
      if (!onlyAttrs(open, allowed)) return rawNode(raw);
      const typeAttr = tag === 'Callout' ? attrStr(open.attrs.type) : null;
      if (tag === 'Callout' && typeAttr === null) return rawNode(raw);
      const title = open.attrs.title === undefined ? null : attrStr(open.attrs.title);
      if (open.attrs.title !== undefined && title === null) return rawNode(raw);
      const calloutType = (tag === 'Callout' ? typeAttr : tag).toLowerCase();
      return `<div data-type="callout-block" data-callout-type="${escAttr(calloutType)}" data-callout-title="${escAttr(
        title || ''
      )}" data-callout-tag="${escAttr(tag)}" data-callout-type-attr="${escAttr(typeAttr || '')}" data-callout-has-title="${
        title !== null
      }">${bodyHtml(inner)}</div>`;
    }

    // ---- steps
    if (tag === 'Steps' && seg.hasClose && onlyAttrs(open, [])) {
      const kids = childBlocks(inner, ['Step']);
      if (!kids || !kids.length) return rawNode(raw);
      let html = '';
      for (const k of kids) {
        if (!k.hasClose || !onlyAttrs(k.open, ['title']) || attrStr(k.open.attrs.title) === null) return rawNode(raw);
        html += `<div data-type="step-item" data-title="${escAttr(k.open.attrs.title)}">${bodyHtml(k.inner)}</div>`;
      }
      return `<div data-type="steps-block">${html}</div>`;
    }

    // ---- accordions: <AccordionGroup><Accordion title/></AccordionGroup>,
    //      <Accordion><AccordionItem title/></Accordion>, single <Accordion title>
    if (tag === 'AccordionGroup' && seg.hasClose && onlyAttrs(open, [])) {
      const kids = childBlocks(inner, ['Accordion']);
      if (!kids || !kids.length) return rawNode(raw);
      let html = '';
      for (const k of kids) {
        if (!k.hasClose || !onlyAttrs(k.open, ['title']) || attrStr(k.open.attrs.title) === null) return rawNode(raw);
        html += `<div data-type="accordion-item" data-title="${escAttr(k.open.attrs.title)}">${bodyHtml(k.inner)}</div>`;
      }
      return `<div data-type="accordion-block" data-flavor="group">${html}</div>`;
    }
    if (tag === 'Accordion' && seg.hasClose) {
      if (onlyAttrs(open, [])) {
        const kids = childBlocks(inner, ['AccordionItem']);
        if (!kids || !kids.length) return rawNode(raw);
        let html = '';
        for (const k of kids) {
          if (!k.hasClose || !onlyAttrs(k.open, ['title']) || attrStr(k.open.attrs.title) === null) return rawNode(raw);
          html += `<div data-type="accordion-item" data-title="${escAttr(k.open.attrs.title)}">${bodyHtml(k.inner)}</div>`;
        }
        return `<div data-type="accordion-block" data-flavor="items">${html}</div>`;
      }
      if (onlyAttrs(open, ['title']) && attrStr(open.attrs.title) !== null) {
        return `<div data-type="accordion-block" data-flavor="single"><div data-type="accordion-item" data-title="${escAttr(
          open.attrs.title
        )}">${bodyHtml(inner)}</div></div>`;
      }
      return rawNode(raw);
    }

    // ---- columns / card groups of <Card>
    if ((tag === 'Columns' || tag === 'CardGroup') && seg.hasClose && onlyAttrs(open, ['cols'])) {
      const cols = open.attrs.cols === undefined ? null : attrNum(open.attrs.cols);
      if (open.attrs.cols !== undefined && cols === null) return rawNode(raw);
      const kids = childBlocks(inner, ['Card', 'iframe']);
      if (!kids || !kids.length) return rawNode(raw);
      let html = '';
      for (const k of kids) {
        if (k.tag === 'iframe') {
          const src = attrStr(k.open.attrs.src) || '';
          const title = attrStr(k.open.attrs.title) || '';
          html += `<div data-type="column-card" data-card-type="iframe" data-title="${escAttr(title || 'Embedded Content')}" data-description="" data-icon="" data-iframe-src="${escAttr(
            src
          )}" data-iframe-html="${escAttr(k.raw)}"></div>`;
          continue;
        }
        const allowedCard = ['title', 'icon', 'href', 'url', 'imagePath', 'cta', 'horizontal'];
        if (!onlyAttrs(k.open, allowedCard)) return rawNode(raw);
        const a = k.open.attrs;
        if (Object.values(a).some((v) => typeof v !== 'string')) return rawNode(raw);
        if (a.href !== undefined && a.url !== undefined) return rawNode(raw);
        const urlKey = a.href !== undefined ? 'href' : 'url';
        const desc = k.hasClose ? dedent(k.inner) : '';
        html += `<div data-type="column-card" data-card-type="card" data-title="${escAttr(a.title || '')}" data-has-title="${
          a.title !== undefined
        }" data-description="${escAttr(desc)}" data-icon="${escAttr(a.icon || '')}" data-url="${escAttr(
          a[urlKey] || ''
        )}" data-url-key="${urlKey}" data-image-path="${escAttr(a.imagePath || '')}" data-cta="${escAttr(
          a.cta || ''
        )}" data-horizontal="${a.horizontal === 'true'}"></div>`;
      }
      return `<div data-type="columns-block" data-tag="${tag}" data-cols="${cols === null ? '' : cols}">${html}</div>`;
    }

    // ---- column layout (editable panes)
    if (tag === 'ColumnLayout' && seg.hasClose && onlyAttrs(open, ['cols'])) {
      const cols = attrNum(open.attrs.cols);
      if (cols === null) return rawNode(raw);
      const kids = childBlocks(inner, ['Col']);
      if (!kids || !kids.length || kids.some((k) => !k.hasClose || !onlyAttrs(k.open, []))) return rawNode(raw);
      const panes = kids.map((k) => `<div data-type="column-pane">${bodyHtml(k.inner)}</div>`).join('');
      return `<div data-type="column-layout" data-cols="${cols}">${panes}</div>`;
    }

    // ---- embeds
    if ((tag === 'YouTube' || tag === 'Loom') && open.selfClosing && onlyAttrs(open, ['id', 'title'])) {
      const id = attrStr(open.attrs.id);
      if (id === null) return rawNode(raw);
      const title = open.attrs.title === undefined ? '' : attrStr(open.attrs.title);
      if (title === null) return rawNode(raw);
      return `<div data-type="embed-block" data-kind="${tag}" data-id="${escAttr(id)}" data-title="${escAttr(title)}"></div>`;
    }
    if (tag === 'iframe') {
      const src = attrStr(open.attrs.src) || '';
      const title = attrStr(open.attrs.title) || '';
      return `<div data-type="iframe-embed" data-src="${escAttr(src)}" data-title="${escAttr(title)}" data-raw-html="${escAttr(raw)}"></div>`;
    }
  } catch (e) {
    // fall through to raw
  }
  return rawNode(raw);
}

/** Markdown/MDX source -> editor-ready HTML */
export function mdToHtml(src) {
  if (!src || !src.trim()) return '';
  return splitBlocks(src)
    .map((s) => (s.type === 'md' ? mdInline(s.text) : convertBlock(s)))
    .join('');
}
